import { DestroyRef, ElementRef, Injectable, OnDestroy, inject } from '@angular/core';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { NavigationEnd, NavigationStart, Router, Scroll } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { SCROLL_TIMEOUT } from './constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';

@Injectable({ providedIn: 'root' })
export class ScrollPositionRestorationService implements OnDestroy {
  private router: Router = inject(Router);
  private viewportScroller: ViewportScroller = inject(ViewportScroller);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private window: Window = inject(WINDOW);
  private document: Document = inject(DOCUMENT);

  private preventScrollTop: boolean = false;
  private preventScrollRestore: boolean = false;
  private anchorToScroll: ElementRef = null;
  private scrollPositionTimeout: number | null = null;
  private scrollAnchorTimeout: number | null = null;
  private readonly pageScrollContainerSelector: string = '[data-page-scroll-container]';
  private readonly pageScrollPositionStore: Map<number, [number, number]> = new Map<number, [number, number]>();
  private readonly maxSavedScrollPositions: number = 50;
  private lastNavigationId: number = 0;
  private restoredNavigationId: number = 0;

  public ngOnDestroy(): void {
    this.scrollPositionTimeout && this.window.clearTimeout(this.scrollPositionTimeout);
    this.scrollAnchorTimeout && this.window.clearTimeout(this.scrollAnchorTimeout);
  }

  /**
   * Подписывается на события роутера и выбирает нужный сценарий скролла:
   * возврат к сохраненной позиции, переход к якорю, отмена автоскролла или скролл наверх.
   */
  public initScroll(): void {
    this.lastNavigationId = this.router.lastSuccessfulNavigation()?.id ?? this.lastNavigationId;

    // Перед уходом со страницы сохраняем позицию внутреннего контейнера,
    // а при back/forward запоминаем navigationId страницы, к которой возвращаемся.
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e: NavigationStart) => {
        this.saveCurrentPageScrollPosition();
        this.restoredNavigationId = e.restoredState?.navigationId ?? 0;
      });

    // После успешной навигации обновляем id текущей страницы. Он станет ключом
    // для сохранения позиции, если пользователь уйдет с этой страницы дальше.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e: NavigationEnd) => {
        this.lastNavigationId = e.id;
      });

    // Scroll приходит уже после навигации. Здесь решаем, что делать именно сейчас:
    // восстановить позицию, перейти к якорю, пропустить автоскролл или подняться наверх.
    this.router.events
      .pipe(
        filter((e): e is Scroll => e instanceof Scroll),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => {
        if (e.position || this.hasRestoredPageScrollPosition()) {
          // Скролл до предыдущей позиции при навигации назад
          if (this.preventScrollRestore) {
            this.preventScrollRestore = false;
            this.restoredNavigationId = 0;
          } else {
            this.scrollPositionTimeout = this.window.setTimeout(() => {
              this.scrollToPosition(this.getRestoredPageScrollPosition() ?? e.position ?? [0, 0]);
              this.restoredNavigationId = 0;
            }, SCROLL_TIMEOUT);
          }
          return;
        } else if (e.anchor) {
          // Скролл к якорю, который указан в ссылке
          // Например, к якорю collection в ссылке https://lenta.com/l/ducks/#collection
          this.scrollAnchorTimeout = this.window.setTimeout(
            () => this.viewportScroller.scrollToAnchor(e.anchor),
            SCROLL_TIMEOUT
          );
          return;
        } else if (this.preventScrollTop) {
          // Отмена скролла вверх
          this.preventScrollTop = false;
          return;
        }

        if (this.anchorToScroll) {
          // Скролл при навигации к якорю, переданному вручную
          this.handleScrollToAnchor();
          this.anchorToScroll = null;
        } else {
          // Скролл при навигации вверх
          this.scrollToPosition([0, 0]);
        }
      });
  }

  public handleScrollToAnchor(): void {
    const anchorRect: DOMRect = this.anchorToScroll?.nativeElement.getBoundingClientRect();
    if (anchorRect?.top < 0) {
      if (this.scrollPageContainerToAnchor(anchorRect)) {
        return;
      }

      this.scrollWindowToAnchor(anchorRect);
    }
  }

  public setScrollAnchor(anchor: ElementRef): void {
    this.anchorToScroll = anchor;
  }

  public setPreventScrollTop(bool: boolean): void {
    this.preventScrollTop = bool;
  }

  public setPreventScrollRestore(bool: boolean): void {
    this.preventScrollRestore = bool;
  }

  /**
   * Скроллит внутренний контейнер к якорю.
   * Если такого контейнера нет, возвращает false, чтобы вызывающий код мог сделать fallback на window.
   */
  private scrollPageContainerToAnchor(anchorRect: DOMRect): boolean {
    const scrollContainer: HTMLElement | undefined = this.getPageScrollContainer();

    if (!scrollContainer) {
      return false;
    }

    const scrollContainerRect: DOMRect = scrollContainer.getBoundingClientRect();
    const scrollTop: number = scrollContainer.scrollTop + anchorRect.top - scrollContainerRect.top;

    scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });

    return true;
  }

  /**
   * Скроллит window к якорю, если на странице нет внутреннего scroll container.
   */
  private scrollWindowToAnchor(anchorRect: DOMRect): void {
    const bodyRect: DOMRect = this.document.body.getBoundingClientRect();
    const scrollTop: number = anchorRect.top - bodyRect.top;

    this.window.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }

  /**
   * Применяет позицию и к window, и к page scroll container.
   * Так стандартное восстановление Angular и кастомные контейнеры ведут себя одинаково.
   */
  private scrollToPosition(position: [number, number]): void {
    this.viewportScroller.scrollToPosition(position);

    this.getPageScrollContainers().forEach((scrollContainer: HTMLElement) => {
      scrollContainer.scrollLeft = position[0];
      scrollContainer.scrollTop = position[1];
    });
  }

  /**
   * Сохраняет позицию текущего page scroll container перед уходом со страницы.
   * Ключом служит id последней успешной навигации, чтобы при back/forward найти страницу-источник.
   */
  private saveCurrentPageScrollPosition(): void {
    const scrollContainer: HTMLElement | undefined = this.getPageScrollContainer();

    if (!scrollContainer) {
      return;
    }

    this.pageScrollPositionStore.set(this.lastNavigationId, [scrollContainer.scrollLeft, scrollContainer.scrollTop]);

    if (this.pageScrollPositionStore.size > this.maxSavedScrollPositions) {
      const oldestNavigationId: number | undefined = this.pageScrollPositionStore.keys().next().value;

      if (oldestNavigationId !== undefined) {
        this.pageScrollPositionStore.delete(oldestNavigationId);
      }
    }
  }

  private getRestoredPageScrollPosition(): [number, number] | null {
    return this.restoredNavigationId ? (this.pageScrollPositionStore.get(this.restoredNavigationId) ?? null) : null;
  }

  private hasRestoredPageScrollPosition(): boolean {
    return !!this.getRestoredPageScrollPosition();
  }

  /**
   * Ищет наши page-обертки, которые можно скроллить вместо window.
   */
  private getPageScrollContainers(): HTMLElement[] {
    return Array.from(this.document.querySelectorAll<HTMLElement>(this.pageScrollContainerSelector)).filter(
      (scrollContainer: HTMLElement) => scrollContainer.isConnected && this.canScrollPageContainer(scrollContainer)
    );
  }

  private getPageScrollContainer(): HTMLElement | undefined {
    return this.getPageScrollContainers()[0];
  }

  /**
   * Проверяет, подходит ли найденная page-обертка как цель скролла.
   * Если нет, сервис будет скроллить window.
   */
  private canScrollPageContainer(scrollContainer: HTMLElement): boolean {
    const overflowY: string = this.window.getComputedStyle(scrollContainer).overflowY;

    return overflowY === 'auto' || overflowY === 'scroll';
  }
}
